import DateFnsUtils from "@date-io/date-fns"
import {
    Button,
    FormControl,
    Grid,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    Tooltip,
} from "@material-ui/core"
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers"
import { ptBR } from "date-fns/locale"
import { useSnackbar, VariantType } from "notistack"
import React, { useEffect, useState } from "react"
import { getTurnosAgendamento } from "../../services/configuracoes-estabelecimentos"
import useLoadingStore from "../../stores/LoadingStore"

const useStyles = makeStyles(theme => ({
    agendar: {
        backgroundColor: "#1976D2",
        color: "white",
        "&:hover": {
            backgroundColor: "#1565C0",
        },
    },
    cancelar: {
        backgroundColor: "#D32F2F",
        color: "white",
        "&:hover": {
            backgroundColor: "#B71C1C",
        },
    },
}))

export enum WeekDay {
    Domingo = "DOMINGO",
    Segunda = "SEGUNDA",
    Terca = "TERCA",
    Quarta = "QUARTA",
    Quinta = "QUINTA",
    Sexta = "SEXTA",
    Sabado = "SABADO",
}

export enum TipoEntrega {
    DeliveryRetirada = 0,
    Delivery = 1,
    Retirada = 2,
}

export interface Turno {
    id: number
    estabelecimentoId: number
    diaSemana: WeekDay
    horaInicio: number
    horaFim: number
    tipoEntrega: TipoEntrega
}

const nowMilliseconds = new Date().setHours(0, 0, 0, 0)
const today = new Date(nowMilliseconds)

const getTime = (time: number) => {
    let hour = Math.floor(time / 60).toString()
    if (hour.length === 1) hour = "0" + hour

    let minute = (time % 60).toString()
    if (minute.length === 1) minute = "0" + minute

    return hour + ":" + minute
}

const diasDaSemana: WeekDay[] = [
    WeekDay.Domingo,
    WeekDay.Segunda,
    WeekDay.Terca,
    WeekDay.Quarta,
    WeekDay.Quinta,
    WeekDay.Sexta,
    WeekDay.Sabado,
]

export function AgendamentoForm({ carrinho, setCarrinho, isRetiradaNoLocal }: any) {
    const classes = useStyles()
    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()
    const alertStart = (msg: string, tipo: VariantType | undefined) => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const [turnos, setTurnos] = useState<Turno[]>([])
    const [turnosPossiveis, setTurnosPossiveis] = useState<Turno[]>([])

    const [isAgendamento, setIsAgendamento] = useState(!!carrinho?.pedido?.dataAgendamentoStart)
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        carrinho?.pedido?.dataAgendamentoStart
            ? new Date(carrinho.pedido.dataAgendamentoStart)
            : today
    )
    const [turnoSelecionado, setTurnoSelecionado] = useState<Turno["id"] | "">("")

    const buscarTurnos = async () => {
        try {
            setLoading(true)
            const response = await getTurnosAgendamento(carrinho.estabelecimento.id)

            setTurnos(response)
        } catch {
            alertStart("Erro ao buscar datas", "error")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (isAgendamento && !turnos.length) {
            buscarTurnos()
        }

        if (isAgendamento && selectedDate && turnos.length) {
            setTurnosPossiveis(
                turnos.filter(
                    t =>
                        t.diaSemana === diasDaSemana[selectedDate.getDay()] &&
                        (isRetiradaNoLocal
                            ? t.tipoEntrega !== TipoEntrega.Delivery
                            : t.tipoEntrega !== TipoEntrega.Retirada)
                )
            )
        } else {
            setTurnosPossiveis([])
        }

        setTurnoSelecionado("")
    }, [isAgendamento, selectedDate, isRetiradaNoLocal, turnos, carrinho.estabelecimento.id])

    const handleChangeTurno = (e: React.ChangeEvent<{ value: unknown }>) => {
        setTurnoSelecionado(e.target.value as Turno["id"])
        const turno = turnosPossiveis.find(t => t.id === e.target.value)

        setCarrinho({
            ...carrinho,

            dataAgendamentoStart: turno
                ? selectedDate!.getTime() + turno.horaInicio * 60 * 1000
                : undefined,
            dataAgendamentoEnd: turno
                ? selectedDate!.getTime() + turno.horaFim * 60 * 1000
                : undefined,
        })
    }

    const horas = new Date().getHours()
    const minutosAtuais = new Date().getMinutes()
    const totalMinutosAgora = horas * 60 + minutosAtuais

    return (
        <div style={{ marginTop: "0.8rem" }}>
            {isAgendamento && (
                <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={6} md={6}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
                            <KeyboardDatePicker
                                fullWidth
                                disableToolbar
                                variant="inline"
                                format="dd/MM/yyyy"
                                margin="normal"
                                label="Data"
                                value={selectedDate}
                                onChange={date => {
                                    setSelectedDate(date)
                                    setTurnoSelecionado("")

                                    setCarrinho({
                                        ...carrinho,
                                        pedido: {
                                            ...carrinho.pedido,
                                            dataAgendamentoStart: undefined,
                                            dataAgendamentoEnd: undefined,
                                        },
                                    })
                                }}
                                minDate={today}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel id="label-agendamento">Horário de agendamento</InputLabel>

                            <Tooltip
                                title={
                                    !selectedDate
                                        ? "Selecione uma data para ver os horários"
                                        : !turnosPossiveis.length
                                        ? "Não temos agendamento para esse dia"
                                        : ""
                                }
                                disableHoverListener={!!turnosPossiveis.length && !!selectedDate}
                            >
                                <span>
                                    <Select
                                        label="Horário de agendamento"
                                        labelId="label-agendamento"
                                        value={turnoSelecionado}
                                        disabled={!selectedDate || !turnosPossiveis.length}
                                        onChange={handleChangeTurno}
                                        fullWidth
                                    >
                                        {turnosPossiveis
                                            .filter(tp => {
                                                return selectedDate?.getDate() === today.getDate()
                                                    ? tp.horaInicio > totalMinutosAgora
                                                    : tp
                                            })
                                            .map(t => (
                                                <MenuItem key={t.id} value={t.id}>
                                                    {getTime(t.horaInicio)} - {getTime(t.horaFim)}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </span>
                            </Tooltip>
                        </FormControl>
                    </Grid>
                </Grid>
            )}

            {!isAgendamento ? (
                <Button
                    onClick={() => setIsAgendamento(p => !p)}
                    variant="contained"
                    className={classes.agendar}
                >
                    Agendar pedido
                </Button>
            ) : (
                <Button
                    onClick={() => {
                        delete carrinho.dataAgendamentoStart
                        delete carrinho.dataAgendamentoEnd
                        setCarrinho({ ...carrinho })
                        setIsAgendamento(p => !p)
                    }}
                    variant="contained"
                    className={classes.cancelar}
                >
                    Cancelar
                </Button>
            )}
        </div>
    )
}
